import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logoutThunk } from "../feature/auth/authAPI";
import { useDispatch, useSelector } from "react-redux";
import { Home, LayoutDashboard, LogOut, ChevronDown, Menu, X, Video } from "lucide-react";
import Avatar from "./ui/Avatar";

const navItems = [
  { name: "Home", path: "/", icon: Home },
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
];

export default function Navbar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const user = useSelector((state) => state.auth.user);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const handleLogout = async () => {
    await dispatch(logoutThunk());
    navigate("/login");
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-white/10 bg-cocoa-800/85 backdrop-blur-xl">
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Interviewly home">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-400 text-cocoa-900 shadow-gold-inset">
            <Video className="h-5 w-5" strokeWidth={2.25} />
          </span>
          <span className="text-xl font-bold tracking-tight text-cream-100">
            Interviewly
          </span>
        </Link>

        <button
          className="flex h-10 w-10 items-center justify-center rounded-lg text-amber-200 transition-colors hover:bg-white/10 md:hidden"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        <div className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-all duration-200 ${
                  active
                    ? "bg-white/10 text-amber-300"
                    : "text-amber-100/70 hover:bg-white/5 hover:text-amber-100"
                }`}
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
                {item.name}
              </Link>
            );
          })}

          <div className="mx-3 h-5 w-px bg-white/10" />

          {user ? (
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu((open) => !open)}
                className="flex items-center gap-2.5 rounded-full py-1 pl-1 pr-2.5 transition-colors hover:bg-white/5"
                aria-haspopup="menu"
                aria-expanded={showProfileMenu}
              >
                <Avatar name={user.name} size="sm" />
                <span className="hidden max-w-[140px] truncate text-sm font-medium text-amber-100 lg:block">
                  {user.name}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-amber-200/70 transition-transform duration-200 ${
                    showProfileMenu ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-64 origin-top-right animate-scale-in rounded-2xl border border-white/10 bg-cocoa-700 py-2 shadow-modal">
                  <div className="border-b border-white/10 px-4 py-3">
                    <p className="text-sm font-semibold text-cream-100">{user.name}</p>
                    <p className="mt-0.5 truncate text-xs text-amber-100/60">{user.email}</p>
                    <p className="mt-1.5">
                      <span className="badge badge-scheduled capitalize">
                        <span className="badge-dot" />
                        {user.role}
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="mt-1 flex w-full items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-red-300 transition-colors hover:bg-white/5"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => navigate("/login")} className="btn btn-primary btn-sm">
              Login
            </button>
          )}
        </div>
      </div>

      {menuOpen && (
        <div className="animate-fade-in border-t border-white/10 bg-cocoa-800 px-4 pb-6 pt-4 md:hidden">
          <div className="flex flex-col gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                    active
                      ? "bg-white/10 text-amber-300"
                      : "text-amber-100/70 hover:bg-white/5 hover:text-amber-100"
                  }`}
                  onClick={() => setMenuOpen(false)}
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                  {item.name}
                </Link>
              );
            })}

            <div className="mt-3 border-t border-white/10 pt-3">
              {user ? (
                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-3">
                    <Avatar name={user.name} size="md" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-cream-100">
                        {user.name}
                      </p>
                      <p className="truncate text-xs text-amber-100/60">{user.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="btn btn-danger btn-md mt-4 w-full"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    navigate("/login");
                    setMenuOpen(false);
                  }}
                  className="btn btn-primary btn-md w-full"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
